import { useEffect, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const today = () => new Date().toISOString().slice(0, 10);

const empty = {
  companyId: 0,
  returnDate: today(),
  returnNo: "",
  invoiceNo: "",
  customerId: "",
  itemId: "",
  qty: 1,
  rate: 0,
  gstPercent: 0,
  reason: "",
};

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 800, margin: 0 },
  sub: { fontSize: 10, opacity: 0.9 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    background: "#07111f",
    color: "#fff",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  stat: {
    border: "1px solid #e9ecef",
    borderRadius: 12,
    padding: 9,
    background: "#f8f9fa",
  },
};

export default function SalesReturnPage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();

  const fyFrom = financialYear?.fromDate;
  const fyTo = financialYear?.toDate;
  const fyLocked = Boolean(financialYear?.isLocked || financialYear?.IsLocked);

  const [form, setForm] = useState({
    ...empty,
    companyId: user?.companyId || 0,
    returnDate: fyFrom || today(),
  });

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [dateLocked, setDateLocked] = useState(false);
  const [checkingDateLock, setCheckingDateLock] = useState(false);

  const companyId = Number(user?.companyId || form.companyId || 0);

  const selectedDateOutsideFy =
    form.returnDate && fyFrom && fyTo
      ? form.returnDate < fyFrom || form.returnDate > fyTo
      : false;

  const isPostingBlocked =
    fyLocked || dateLocked || selectedDateOutsideFy || checkingDateLock;

  useEffect(() => {
    if (fyFrom) {
      setForm((p) => ({
        ...p,
        returnDate: fyFrom,
      }));
    }
  }, [fyFrom]);

  const checkReturnDateLock = async (returnDate) => {
    if (!returnDate || !companyId) {
      setDateLocked(false);
      return;
    }

    try {
      setCheckingDateLock(true);

      const res = await client.get("/finance/is-date-locked", {
        params: {
          entryDate: returnDate,
          companyId,
        },
      });

      setDateLocked(Boolean(res.data?.isLocked));
    } catch (err) {
      console.error("Date lock check failed", err);
      setDateLocked(false);
    } finally {
      setCheckingDateLock(false);
    }
  };

  useEffect(() => {
    checkReturnDateLock(form.returnDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.returnDate, companyId]);

  const loadMasters = async () => {
    try {
      const [c, i] = await Promise.all([
        client.get("/sales/customers", {
          params: companyId ? { companyId } : {},
        }),
        client.get("/inventory/items", {
          params: companyId ? { companyId } : {},
        }),
      ]);

      setCustomers(Array.isArray(c.data) ? c.data : []);
      setItems(Array.isArray(i.data) ? i.data : []);
    } catch (e) {
      console.error("Master load error:", e);
    }
  };

  const load = async () => {
    setLoading(true);
    setMessage("");

    try {
      const params = { companyId };

      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.search) params.search = filters.search;

      const res = await client.get("/salesreturns", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setMessage(
        e?.response?.data?.message ||
          e?.response?.data ||
          e.message ||
          "Unable to load sales returns.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMasters();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleItemChange = (id) => {
    const item = items.find((x) => String(x.Id ?? x.id) === String(id));

    setForm((p) => ({
      ...p,
      itemId: id,
      rate: Number(item?.SalePrice ?? item?.salePrice ?? p.rate ?? 0),
      gstPercent: Number(
        item?.GSTPercent ?? item?.gstPercent ?? p.gstPercent ?? 0,
      ),
    }));
  };

  const save = async (e) => {
    e.preventDefault();

    if (checkingDateLock)
      return alert("Please wait. Checking financial year lock status.");

    if (fyLocked)
      return alert(
        "Selected financial year is locked. Sales return cannot be posted.",
      );

    if (dateLocked)
      return alert("Selected return date belongs to locked financial year.");

    if (selectedDateOutsideFy)
      return alert("Return date must be within the selected financial year.");

    setSaving(true);
    setMessage("");

    try {
      const payload = {
        ...form,
        companyId,
        customerId: Number(form.customerId),
        itemId: Number(form.itemId),
        qty: Number(form.qty || 0),
        rate: Number(form.rate || 0),
        gstPercent: Number(form.gstPercent || 0),
      };

      const res = await client.post("/salesreturns", payload);

      setMessage(
        res.data?.Message ||
          res.data?.message ||
          "Sales return saved. Stock and ledger updated.",
      );

      setForm({
        ...empty,
        companyId,
        returnDate: fyFrom || today(),
      });

      await load();
    } catch (e) {
      setMessage(
        e?.response?.data?.message ||
          e?.response?.data ||
          e.message ||
          "Failed to save sales return.",
      );
    } finally {
      setSaving(false);
    }
  };

  const clearForm = () => {
    setForm({
      ...empty,
      companyId,
      returnDate: fyFrom || today(),
    });
    setMessage("");
  };

  const selectedItem = items.find(
    (x) => String(x.Id ?? x.id) === String(form.itemId),
  );

  const taxable = Number(form.qty || 0) * Number(form.rate || 0);
  const tax = (taxable * Number(form.gstPercent || 0)) / 100;
  const total = taxable + tax;

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div className="mb-2" style={styles.card}>
        <div style={styles.header}>
          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <div>
              <h6 style={styles.title}>
                <i className="bi bi-arrow-return-left me-2"></i>
                Sales Return
              </h6>
              <div style={styles.sub}>
                Return entry with stock increase, journal posting and
                transaction log.
              </div>
            </div>

            <button
              className="btn btn-light btn-sm"
              style={styles.btn}
              onClick={load}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="row g-2">
        <div className="col-lg-4">
          <div style={styles.card}>
            <div className="border-bottom bg-light px-3 py-2 fw-bold">
              <i className="bi bi-pencil-square me-1"></i>
              New Sales Return
            </div>

            <div style={styles.body}>
              {checkingDateLock && (
                <div
                  className="alert alert-info py-2 mb-2"
                  style={{ fontSize: 11 }}
                >
                  Checking financial year lock status...
                </div>
              )}

              {fyLocked && (
                <div
                  className="alert alert-danger py-2 mb-2"
                  style={{ fontSize: 11 }}
                >
                  <i className="bi bi-lock-fill me-1"></i>
                  Selected financial year is locked. Sales return cannot be
                  posted.
                </div>
              )}

              {dateLocked && (
                <div
                  className="alert alert-danger py-2 mb-2"
                  style={{ fontSize: 11 }}
                >
                  <i className="bi bi-lock-fill me-1"></i>
                  Selected return date belongs to locked financial year.
                </div>
              )}

              {selectedDateOutsideFy && (
                <div
                  className="alert alert-warning py-2 mb-2"
                  style={{ fontSize: 11 }}
                >
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Return date must be between {fyFrom} and {fyTo}.
                </div>
              )}

              <form onSubmit={save}>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label style={styles.label}>Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={styles.input}
                      min={fyFrom || undefined}
                      max={fyTo || undefined}
                      value={form.returnDate}
                      onChange={(e) => set("returnDate", e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label style={styles.label}>Return No</label>
                    <input
                      className="form-control form-control-sm"
                      style={styles.input}
                      value={form.returnNo}
                      disabled={isPostingBlocked}
                      onChange={(e) => set("returnNo", e.target.value)}
                      placeholder="Auto if blank"
                    />
                  </div>

                  <div className="col-md-6">
                    <label style={styles.label}>Invoice No</label>
                    <input
                      className="form-control form-control-sm"
                      style={styles.input}
                      value={form.invoiceNo}
                      disabled={isPostingBlocked}
                      onChange={(e) => set("invoiceNo", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label style={styles.label}>Customer</label>
                    <select
                      className="form-select form-select-sm"
                      style={styles.input}
                      value={form.customerId}
                      disabled={isPostingBlocked}
                      onChange={(e) => set("customerId", e.target.value)}
                      required
                    >
                      <option value="">Select</option>
                      {customers.map((c) => (
                        <option key={c.Id ?? c.id} value={c.Id ?? c.id}>
                          {c.CustomerName ?? c.customerName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label style={styles.label}>Item</label>
                    <select
                      className="form-select form-select-sm"
                      style={styles.input}
                      value={form.itemId}
                      disabled={isPostingBlocked}
                      onChange={(e) => handleItemChange(e.target.value)}
                      required
                    >
                      <option value="">Select</option>
                      {items.map((it) => (
                        <option key={it.Id ?? it.id} value={it.Id ?? it.id}>
                          {it.ItemCode ?? it.itemCode} -{" "}
                          {it.ItemName ?? it.itemName}
                        </option>
                      ))}
                    </select>

                    {selectedItem && (
                      <div className="small text-muted mt-1">
                        HSN:{" "}
                        {(selectedItem.HSNCode ?? selectedItem.hsnCode) || "-"}
                      </div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label style={styles.label}>Qty</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm text-end"
                      style={styles.input}
                      value={form.qty}
                      disabled={isPostingBlocked}
                      onChange={(e) => set("qty", e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label style={styles.label}>Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm text-end"
                      style={styles.input}
                      value={form.rate}
                      disabled={isPostingBlocked}
                      onChange={(e) => set("rate", e.target.value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label style={styles.label}>GST %</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm text-end"
                      style={styles.input}
                      value={form.gstPercent}
                      disabled={isPostingBlocked}
                      onChange={(e) => set("gstPercent", e.target.value)}
                    />
                  </div>

                  <div className="col-12">
                    <label style={styles.label}>Reason</label>
                    <input
                      className="form-control form-control-sm"
                      style={styles.input}
                      value={form.reason}
                      disabled={isPostingBlocked}
                      onChange={(e) => set("reason", e.target.value)}
                      placeholder="Defective / Wrong item / Rate issue"
                    />
                  </div>
                </div>

                <div className="row g-2 my-2">
                  {[
                    ["Taxable", taxable],
                    ["GST", tax],
                    ["Total", total],
                  ].map(([label, value]) => (
                    <div className="col-4" key={label}>
                      <div style={styles.stat}>
                        <div className="text-muted" style={{ fontSize: 10 }}>
                          {label}
                        </div>
                        <div className="fw-bold text-primary">
                          ₹ {value.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {message && (
                  <div
                    className="alert alert-info py-2 mb-2"
                    style={{ fontSize: 11 }}
                  >
                    {String(message)}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary btn-sm flex-fill"
                    style={styles.btn}
                    disabled={saving || isPostingBlocked}
                  >
                    <i className="bi bi-save me-1"></i>
                    {saving ? "Saving..." : "Save Return"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    style={styles.btn}
                    onClick={clearForm}
                    disabled={saving}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div style={styles.card}>
            <div style={styles.body}>
              <div className="row g-2 align-items-end mb-2">
                <div className="col-md-3">
                  <label style={styles.label}>From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={styles.input}
                    value={filters.fromDate}
                    onChange={(e) =>
                      setFilters({ ...filters, fromDate: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label style={styles.label}>To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={styles.input}
                    value={filters.toDate}
                    onChange={(e) =>
                      setFilters({ ...filters, toDate: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-4">
                  <label style={styles.label}>Search</label>
                  <input
                    className="form-control form-control-sm"
                    style={styles.input}
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                    placeholder="Return, invoice, customer, item"
                  />
                </div>

                <div className="col-md-2 d-grid">
                  <button
                    className="btn btn-dark btn-sm"
                    style={styles.btn}
                    onClick={load}
                    disabled={loading}
                  >
                    <i className="bi bi-search me-1"></i>
                    Load
                  </button>
                </div>
              </div>

              <div
                className="d-flex justify-content-between mb-2"
                style={{ fontSize: 10, color: "#6c757d" }}
              >
                <span>
                  Total Records: <strong>{rows.length}</strong>
                </span>
                <span>Stock + Journal posted returns</span>
              </div>

              <div style={{ maxHeight: 500, overflow: "auto" }}>
                <table className="table table-sm table-hover table-bordered align-middle mb-0">
                  <thead>
                    <tr>
                      {[
                        "Date",
                        "Return",
                        "Invoice",
                        "Customer",
                        "Item",
                        "Qty In",
                        "Amount",
                        "Journal",
                        "Reason",
                      ].map((h) => (
                        <th
                          key={h}
                          style={styles.th}
                          className={
                            ["Qty In", "Amount"].includes(h) ? "text-end" : ""
                          }
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="9" className="text-center py-3">
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length ? (
                      rows.map((r, i) => (
                        <tr key={r.Id ?? r.id ?? i}>
                          <td style={styles.td}>
                            {String(r.ReturnDate ?? r.returnDate ?? "").slice(
                              0,
                              10,
                            )}
                          </td>
                          <td style={styles.td}>{r.ReturnNo ?? r.returnNo}</td>
                          <td style={styles.td}>
                            {r.InvoiceNo ?? r.invoiceNo ?? "-"}
                          </td>
                          <td style={styles.td}>
                            {r.CustomerName ?? r.customerName ?? "-"}
                          </td>
                          <td style={styles.td}>
                            {r.ItemName ?? r.itemName ?? "-"}
                          </td>
                          <td className="text-end" style={styles.td}>
                            {Number(r.Qty ?? r.qty ?? 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold" style={styles.td}>
                            {Number(r.Amount ?? r.amount ?? 0).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            {(r.JournalEntryId ?? r.journalEntryId) ? (
                              <span className="badge text-bg-success">
                                Posted
                              </span>
                            ) : (
                              <span className="badge text-bg-secondary">-</span>
                            )}
                          </td>
                          <td style={{ ...styles.td, minWidth: 160 }}>
                            {r.Reason ?? r.reason ?? "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-3">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="small text-muted mt-2">
                Each save creates StockTransactions Qty In +
                JournalEntries/JournalDetails + TransactionLogs.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
